import React, {useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'

import {Searchbox as GnomadSearchbox} from '@gnomad/ui'

import {fetchSearchResults} from './search'
import {useLocation, useNavigate} from 'react-router-dom'
import {ADD_RECENT_SEARCH_ITEM, TOGGLE_SEARCH_ERROR} from '@/store/types'
import {useDispatch, useSelector} from 'react-redux'
import {gql, useApolloClient, useQuery} from '@apollo/client'
import {InitialStateType} from '@/store/reducers'
import Alert from '@mui/material/Alert'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import {FormControl, LinearProgress, MenuItem, Select, Snackbar} from '@mui/material'
import {DatasetInfo} from '@/generated/graphql'

const SearchBoxWrapper = styled.div`
    display: flex;
    justify-content: right;
    align-items: end;

    @media (max-width: 1160px) {
        display: none;
    }
    padding-right: 2em;
`

const SearchBoxInner = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    color: ${ThemeColorsHospitalic.TEXT_PRIMARY};
`

const GnomadSearchBoxWrapper = styled.div`
    input {
        height: 40px;
        background-color: white;
        border: 1px solid ${ThemeColorsHospitalic.PRIMARY_MAIN};
    }
`

const StyledSelect = styled(Select)`
    background-color: white;
    box-shadow: none;
    border: 1px solid ${ThemeColorsHospitalic.PRIMARY_MAIN};

    fieldset {
        border: ${props => !props.error && 'none !important;'};
        outline: ${props => !props.error && 'none !important;'};
    }

    margin-right: 0.4em;
    margin-left: 1em;
    height: 40px;
`

const NoDatasetWrapper = styled.em<{ isError: boolean }>`
    color: ${props => props.isError && 'red'};
`

const DATASET_LIST_QUERY = gql`
    query DatasetList {
        datasetList {
            dataset_id,
            dataset_name
        }
    }
`

type Props = {
    placeholder?: string
}

export default function SearchBox({
    placeholder = 'Search by gene, region, transcript or variant',
    ...rest
}: Props) {
    const [currentDataSet, setCurrentDataSet] = useState<string>('placeholder')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const client = useApolloClient()
    const {pathname} = useLocation()
    const {data, loading: isLoading, error: datasetError} = useQuery(DATASET_LIST_QUERY)

    // Update search dataset when active dataset changes.
    // Cannot rely on props for this because the top bar does not re-render.

    const innerSearchbox = useRef(null)

    useEffect(() => {
        if (data && data.length > 0) {
            setCurrentDataSet(data[0].dataset_id)
        }
    }, [data])

    const error = useSelector((state: InitialStateType) => state.search.error)

    const getPlaceholder = useCallback((isLoading: boolean, isError: boolean, data: any) => {
        if (isLoading) {
            return <LinearProgress/>
        } else if (isError || data.length === 0) {
            return <NoDatasetWrapper isError={isError}>--No Datasets--</NoDatasetWrapper>
        } else {
            return <em>--Select Dataset--</em>
        }
    }, [])

    return (
        <>
            {error && (
                <Snackbar open={error}
                    autoHideDuration={4000}
                    onClose={() => dispatch({type: TOGGLE_SEARCH_ERROR})}
                    anchorOrigin={{vertical: 'top', horizontal: 'right'}}>
                    <Alert severity='error' variant='filled'>
                        Entered range is too big!
                    </Alert>
                </Snackbar>
            )}
            <SearchBoxWrapper>
                <SearchBoxInner>
                    <FormControl sx={{minWidth: '15em', maxWidth: '18em'}} size="small">
                        <StyledSelect
                            labelId="dataset-select-label"
                            id="dataset-select"
                            value={currentDataSet}
                            onChange={(evt) => setCurrentDataSet(evt.target.value as string)}
                            inputProps={{'aria-label': 'Without label'}}
                            error={!!datasetError}
                        >
                            <MenuItem key="dataset-placeholder" disabled value="placeholder">
                                {getPlaceholder(isLoading, !!datasetError, data)}
                            </MenuItem>
                            {data?.datasetList?.map((datasetInfo: DatasetInfo) => <MenuItem key={datasetInfo.dataset_id}
                                value={datasetInfo.dataset_id}>{datasetInfo.dataset_name}</MenuItem>)}
                        </StyledSelect>
                    </FormControl>
                    <GnomadSearchBoxWrapper>
                        <GnomadSearchbox
                            // Clear input when URL changes
                            key={pathname}
                            {...rest}
                            ref={innerSearchbox}
                            fetchSearchResults={query =>
                                fetchSearchResults(query, client, dispatch, currentDataSet)
                            }
                            placeholder={placeholder}

                            width={415}
                            onSelect={(url: any, item) => {
                                navigate(url)
                                dispatch(
                                    ADD_RECENT_SEARCH_ITEM({
                                        label: item.label,
                                        url: item.value,
                                    })
                                )
                            }}
                        />
                    </GnomadSearchBoxWrapper>
                </SearchBoxInner>
            </SearchBoxWrapper>
        </>
    )
}
